import { Button } from "@bindery/ui/components/ui/button";
import { useMemo } from "react";
import { startReadingHref } from "@/auth/links";
import { LibraryShell } from "@/components/design-switcher";
import { type SessionStore, sessionStore, useSession } from "@/convex/session";
import {
  BookmarkList,
  selectBookmarksNewestFirst,
} from "@/library/bookmark-list";
import { libraryBooks } from "@/library/books";
import { DailyRecommendationsSection } from "@/library/daily-recommendations";
import { useBookmarks } from "@/library/use-bookmarks";
import { useDailyRecommendations } from "@/library/use-daily-recommendations";
import type { BookmarkValue } from "@/storage/db";

export const CONTINUE_READING_LIMIT = 3;

function ContinueReading({
  bookmarks,
}: {
  bookmarks: readonly BookmarkValue[];
}) {
  if (selectBookmarksNewestFirst(bookmarks, "in-progress").length === 0)
    return null;
  return (
    <section className="bg-stone-50 px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
          Continue reading
        </h2>
        <p className="mt-1 text-gray-600">Pick up where you left off</p>
        <div className="mt-6">
          <BookmarkList
            bookmarks={bookmarks}
            filter="in-progress"
            limit={CONTINUE_READING_LIMIT}
          />
        </div>
      </div>
    </section>
  );
}

function StartReading() {
  return (
    <section className="bg-stone-50 px-6 py-6 text-center">
      <p className="mb-3 text-gray-700">Pick a book and start reading.</p>
      <Button asChild>
        <a href={startReadingHref()}>Start reading</a>
      </Button>
    </section>
  );
}

export function LibraryPage({
  store = sessionStore,
}: {
  store?: SessionStore;
}) {
  const session = useSession(store);
  const bookmarks = useBookmarks();
  const books = useMemo(() => libraryBooks(bookmarks), [bookmarks]);
  const recommendations = useDailyRecommendations();

  return (
    <>
      {session.status === "signed-out" && <StartReading />}
      <ContinueReading bookmarks={bookmarks} />
      <DailyRecommendationsSection {...recommendations} />
      <LibraryShell books={books} />
    </>
  );
}
